import { useContext, useEffect, useState } from "react"
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { DateTime } from "luxon"
import { useLocalStorage } from "usehooks-ts"

import arrowsIcon from "#/assets/arrow-left-right.svg"
import checkIcon from "#/assets/check-dark-green.svg"
import crossBlack from "#/assets/cross-black.svg"
import linkIcon from "#/assets/external-link-arrow-yellow.svg"
import {
  selectMigratedDeposits,
  selectMigratingDeposits,
  useDappSelector,
} from "#/redux-state"
import TooltipLabel from "#/shared/components/TooltipLabel"
import {
  FRIENDLY_DECIMALS_AMOUNT,
  links,
  LOCAL_STORAGE_MIGRATIONS_SEEN,
} from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import { bigIntToHumanReadableFormat } from "#/shared/utils"

const DAYS_TO_COMPLETE_MIGRATION = 14

function WbtcTbtcMigrationInProgressBanner({
  amount,
  token,
  requestedAt,
}: {
  amount: string
  token: string
  requestedAt?: string
}) {
  const estimatedDate = requestedAt
    ? DateTime.fromSQL(requestedAt)
        .plus({ days: DAYS_TO_COMPLETE_MIGRATION })
        .toFormat("dd MMM yyyy")
        .toUpperCase()
    : "in 7 days"

  return (
    <Box bg="#F7F6DB" borderRadius="16px">
      <Flex
        gap="23px"
        p="16px 28px"
        fontFamily="riforma.medium"
        fontWeight={500}
        lineHeight="24px"
        letterSpacing="0.16px"
      >
        <Image src={arrowsIcon} />
        <Flex flexDir="column">
          <Text color="#5C4F09" fontSize="18px">
            <TooltipLabel
              value={`${bigIntToHumanReadableFormat(
                BigInt(amount),
                18,
                18,
              )} ${token}`}
            >
              {bigIntToHumanReadableFormat(
                BigInt(amount),
                18,
                FRIENDLY_DECIMALS_AMOUNT,
              )}
            </TooltipLabel>{" "}
            {token} Migration in progress
          </Text>
          <Text color="#766C37" fontSize="16px">
            Estimated completion: {estimatedDate}. When completed your {token}{" "}
            deposit will be changed to a tBTC deposit.
            <Link
              to={links.MIGRATIONS_DOCS}
              style={{
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
                borderBottom: "1px solid #766C37",
                marginLeft: "3px",
              }}
              target="_blank"
            >
              <Text color="#766C37" variant="body" as="span" lineHeight="20px">
                Read more
              </Text>
              <Image src={linkIcon} />
            </Link>
          </Text>
        </Flex>
      </Flex>
    </Box>
  )
}

function WbtcTbtcMigrationFinishedBanner({
  onDismiss,
}: {
  onDismiss: () => void
}) {
  return (
    <Box bg="#D4F8ED" borderRadius="16px">
      <Flex gap="23px" p="16px 28px" alignItems="center">
        <Image src={checkIcon} />
        <Flex
          flexDir="column"
          fontFamily="riforma.medium"
          fontWeight={500}
          lineHeight="24px"
          letterSpacing="0.16px"
        >
          <Text color="#195237" fontSize="18px">
            Migration completed
          </Text>
          <Text color="#247A52" fontSize="16px">
            Your WBTC deposit is now a tBTC one.
          </Text>
        </Flex>
        <Button
          variant="unstyled"
          onClick={onDismiss}
          h="24px"
          w="24px"
          ml="auto"
        >
          <Image src={crossBlack} />
        </Button>
      </Flex>
    </Box>
  )
}

export default function WbtcTbtcMigrationStatusBanners() {
  const [migrationsSeen, setMigrationsSeen] = useLocalStorage<string[]>(
    LOCAL_STORAGE_MIGRATIONS_SEEN,
    [],
  )

  const migratingDeposits = useDappSelector(selectMigratingDeposits)
  const migratedDeposits = useDappSelector(selectMigratedDeposits)

  const [showFinishedBanner, setShowFinishedBanner] = useState(false)

  const { isEthereumFlow } = useContext(FlowContext)

  useEffect(() => {
    const unseenMigrations = migratedDeposits.filter(
      (deposit) =>
        deposit.migration?.id &&
        !migrationsSeen.includes(deposit.migration?.id),
    )

    if (unseenMigrations.length > 0) {
      setShowFinishedBanner(true)
    }
  }, [migratedDeposits, migrationsSeen])

  const handleDismiss = () => {
    setMigrationsSeen(
      migratedDeposits.flatMap((deposit) => deposit.migration?.id ?? []),
    )
    setShowFinishedBanner(false)
  }

  if (!isEthereumFlow) return null

  return (
    <Flex gap="26px" flexDir="column" mb="26px">
      {showFinishedBanner && (
        <WbtcTbtcMigrationFinishedBanner onDismiss={handleDismiss} />
      )}
      {migratingDeposits.map((deposit) => (
        <WbtcTbtcMigrationInProgressBanner
          key={deposit.depositId}
          amount={deposit.amount}
          token={deposit.token}
          requestedAt={deposit.migration?.requestedAt}
        />
      ))}
    </Flex>
  )
}
